import { requireNodeEnvVar } from "../server/utils";

export type RecurringSubscriptionStatus =
  | "past_due"
  | "cancel_at_period_end"
  | "active"
  | "deleted";
export type ActiveRecurringSubscriptionStatus = Exclude<
  RecurringSubscriptionStatus,
  "deleted"
>;

export type SubscriptionStatus = RecurringSubscriptionStatus | "paid_once";

export const supporterEntitlementStatuses: SubscriptionStatus[] = [
  "active",
  "cancel_at_period_end",
  "paid_once",
];

export function isRecurringSubscriptionStatus(
  subscriptionStatus: string | null | undefined,
): subscriptionStatus is RecurringSubscriptionStatus {
  return (
    subscriptionStatus === "past_due" ||
    subscriptionStatus === "cancel_at_period_end" ||
    subscriptionStatus === "active" ||
    subscriptionStatus === "deleted"
  );
}

export function isActiveRecurringSubscriptionStatus(
  subscriptionStatus: string | null | undefined,
): subscriptionStatus is ActiveRecurringSubscriptionStatus {
  return (
    subscriptionStatus === "past_due" ||
    subscriptionStatus === "cancel_at_period_end" ||
    subscriptionStatus === "active"
  );
}

function getSupporterPlanId(
  oneTimeEnvVarName: string,
  recurringFallbackEnvVarName: string,
): string {
  // Keep checkout available while one-time Stripe price ids roll out.
  return (
    process.env[oneTimeEnvVarName] ??
    requireNodeEnvVar(recurringFallbackEnvVarName)
  );
}

function hasEnvVar(envVarName: string): boolean {
  const value = process.env[envVarName];
  return typeof value === "string" && value.length > 0;
}

function getSupporterPlanIdsForLookup(
  oneTimeEnvVarName: string,
  recurringFallbackEnvVarName: string,
): string[] {
  return [
    process.env[oneTimeEnvVarName],
    process.env[recurringFallbackEnvVarName],
  ].filter(
    (priceId): priceId is string =>
      typeof priceId === "string" && priceId.length > 0,
  );
}

export enum PaymentPlanId {
  Free = "free",
  Supporter = "supporter",
  SuperSupporter = "super-supporter",
}

export interface PaymentPlan {
  // Returns the id under which this payment plan is identified on your payment processor.
  // E.g. this might be price id on Stripe, or variant id on LemonSqueezy.
  getPaymentProcessorPlanId: () => string;
  effect: PaymentPlanEffect;
}

export type PaymentPlanEffect =
  | { kind: "subscription" }
  | { kind: "one_time_supporter" }
  | { kind: "credits"; amount: number };

export const paymentPlans: Record<PaymentPlanId, PaymentPlan> = {
  [PaymentPlanId.Free]: {
    getPaymentProcessorPlanId: () =>
      requireNodeEnvVar("PAYMENTS_FREE_SUBSCRIPTION_PLAN_ID"),
    effect: { kind: "subscription" },
  },
  [PaymentPlanId.Supporter]: {
    getPaymentProcessorPlanId: () =>
      getSupporterPlanId(
        "PAYMENTS_SUPPORTER_ONE_TIME_PLAN_ID",
        "PAYMENTS_SUPPORTER_SUBSCRIPTION_PLAN_ID",
      ),
    effect: { kind: "one_time_supporter" },
  },
  [PaymentPlanId.SuperSupporter]: {
    getPaymentProcessorPlanId: () =>
      getSupporterPlanId(
        "PAYMENTS_SUPER_SUPPORTER_ONE_TIME_PLAN_ID",
        "PAYMENTS_SUPER_SUPPORTER_SUBSCRIPTION_PLAN_ID",
      ),
    effect: { kind: "one_time_supporter" },
  },
};

export function isSupporterPlanUsingRecurringFallback(
  planId: PaymentPlanId,
): boolean {
  switch (planId) {
    case PaymentPlanId.Supporter:
      return !hasEnvVar("PAYMENTS_SUPPORTER_ONE_TIME_PLAN_ID");
    case PaymentPlanId.SuperSupporter:
      return !hasEnvVar("PAYMENTS_SUPER_SUPPORTER_ONE_TIME_PLAN_ID");
    default:
      return false;
  }
}

export function getPaymentProcessorPlanIdsForLookup(
  planId: PaymentPlanId,
): string[] {
  switch (planId) {
    case PaymentPlanId.Free:
      return [paymentPlans[planId].getPaymentProcessorPlanId()];
    case PaymentPlanId.Supporter:
      return getSupporterPlanIdsForLookup(
        "PAYMENTS_SUPPORTER_ONE_TIME_PLAN_ID",
        "PAYMENTS_SUPPORTER_SUBSCRIPTION_PLAN_ID",
      );
    case PaymentPlanId.SuperSupporter:
      return getSupporterPlanIdsForLookup(
        "PAYMENTS_SUPER_SUPPORTER_ONE_TIME_PLAN_ID",
        "PAYMENTS_SUPER_SUPPORTER_SUBSCRIPTION_PLAN_ID",
      );
  }
}

export function prettyPaymentPlanName(planId: PaymentPlanId): string {
  const planToName: Record<PaymentPlanId, string> = {
    [PaymentPlanId.Free]: "Free",
    [PaymentPlanId.Supporter]: "Supporter",
    [PaymentPlanId.SuperSupporter]: "Super Supporter",
  };
  return planToName[planId];
}

export function parsePaymentPlanId(planId: string): PaymentPlanId {
  if ((Object.values(PaymentPlanId) as string[]).includes(planId)) {
    return planId as PaymentPlanId;
  } else {
    throw new Error(`Invalid PaymentPlanId: ${planId}`);
  }
}

export function getSubscriptionPaymentPlanIds(): PaymentPlanId[] {
  // Keep seed distribution stable across Free/Supporter tiers, even while
  // supporter plans can be configured as one-time in runtime behavior.
  return Object.values(PaymentPlanId);
}
